<template>
  <div>
    <div
      v-if="transactionCount === 0"
      class="row justify-center q-mt-lg"
    >
      <q-banner
        class="bg-pastel-blue text-center q-pa-lg col-2"
        dense
      >
        <template v-slot:avatar>
          <q-icon
            name="las la-info-circle"
            class="message-box-text"
            size="sm"
          />
        </template>
        <div class="">
          {{ $q.lang.table.noResults }}
        </div>
      </q-banner>
    </div>

    <!-- Table Top Pagination -->
    <div
      v-if="transactionCount > 0"
      class="q-table__bottom row text-default items-center justify-end border-none transactions-pagination-top"
    >
      <div class="q-table__control col-12">
        <Pagination
          :limits="[10, 20, 50, 100, 150, 200]"
          :meta="state_transactions_meta"
          :prop_keys="['transactions']"
          @click="transactions_pagination_update"
        />
      </div>
    </div>

    <!-- Transactions Table -->
    <div class="fit row wrap justify-start items-start content-start align-start">
      <q-markup-table
        v-if="transactionCount !== 0"
        id="transactions_table"
        class="full-width q-pa-none app-table"
        flat
        separator="none"
      >
        <thead>
          <tr>
            <th class="text-left q-table--col-auto-width">
              <q-checkbox
                :model-value="transactions_page_selected"
                color="accent"
                @update:model-value="transactions_page_select"
              />
            </th>
            <th
              class="text-left sortable"
              :class="{ sorted: state_search_params.order_by == 'time' }"
              @click="transactions_filter_add({ order_by: 'time', order_direction: state_search_params.order_direction == 'asc' ? 'desc' : 'asc' })"
            >
              {{ i18n.transactions_headers_date }}
              <i
                class="q-icon notranslate material-symbols-outlined q-table__sort-icon q-table__sort-icon--left"
                aria-hidden="true"
                role="presentation"
              >
                {{ state_search_params.order_direction == 'asc' ? 'arrow_downward' : 'arrow_upward' }}
              </i>
            </th>
            <th
              class="text-left sortable"
              :class="{ sorted: state_search_params.order_by == 'kind' }"
              @click="transactions_filter_add({ order_by: 'kind', order_direction: state_search_params.order_direction == 'asc' ? 'desc' : 'asc' })"
            >
              {{ i18n.transactions_headers_kind }}
              <i
                class="q-icon notranslate material-symbols-outlined q-table__sort-icon q-table__sort-icon--left"
                aria-hidden="true"
                role="presentation"
              >
                {{ state_search_params.order_direction == 'asc' ? 'arrow_downward' : 'arrow_upward' }}
              </i>
            </th>
            <th
              class="text-right sortable"
              :class="{ sorted: state_search_params.order_by == 'amount' }"
              @click="transactions_filter_add({ order_by: 'amount', order_direction: state_search_params.order_direction == 'asc' ? 'desc' : 'asc' })"
            >
              {{ i18n.transactions_headers_quantity }}
              <i
                class="q-icon notranslate material-symbols-outlined q-table__sort-icon q-table__sort-icon--left"
                aria-hidden="true"
                role="presentation"
              >
                {{ state_search_params.order_direction == 'asc' ? 'arrow_downward' : 'arrow_upward' }}
              </i>
            </th>
            <th
              v-if="workspace_settings.settings_tr_counter_value_col"
              class="text-right"
            >
              {{ i18n.transactions_headers_fiat }}
            </th>
            <th
              v-if="workspace_settings.settings_tr_wac_col"
              class="text-right"
            >
              {{ i18n.transactions_headers_wac }}
            </th>
            <th
              v-if="workspace_settings.settings_tr_balance_col"
              class="text-right sortable"
              :class="{ sorted: state_search_params.order_by == 'quantity_after_movement' }"
              @click="transactions_filter_add({ order_by: 'quantity_after_movement', order_direction: state_search_params.order_direction == 'asc' ? 'desc' : 'asc' })"
            >
              {{ i18n.transactions_headers_balance }}
              <i
                class="q-icon notranslate material-symbols-outlined q-table__sort-icon q-table__sort-icon--left"
                aria-hidden="true"
                role="presentation"
              >
                {{ state_search_params.order_direction == 'asc' ? 'arrow_downward' : 'arrow_upward' }}
              </i>
            </th>
            <th class="text-right">
              {{ i18n.transactions_headers_sender }}
            </th>
            <th class="text-center">
              {{ i18n.transactions_headers_recipient }}
            </th>
            <th
              v-if="workspace_settings.settings_tr_info_col"
              class="text-center"
            />
            <th
              v-if="workspace_settings.settings_tr_import_source_col"
              class="text-center"
            >
              {{ i18n.transactions_headers_import_source }}
            </th>
            <th class="actions-column" />
          </tr>
        </thead>
        <tbody>
          <!-- Transactions Table Loading Bar -->
          <tr style="padding: 0px; height:1px">
            <td
              colspan="100"
              style="padding: 0px; height:1px"
            >
              <q-linear-progress
                v-if="transactions_loading"
                color="primary"
                indeterminate
                size="1px"
              />
            </td>
          </tr>

          <!-- Top Row / Select All Transactions -->
          <template v-if="transactions_page_selected">
            <tr>
              <td
                colspan="100"
                class="table-select-all-row"
              >
                <div class="fit row wrap justify-center items-center content-start align-start q-pa-xs">
                  <template v-if="transactions_all_pages_selected == false">
                    <span v-html="i18n.transactions_selected_some.replace('%{0}', transactions_selected_count)" />
                    <q-btn
                      :label="i18n.transactions_select_all_action.replace('%{0}', transactions.meta.count)"
                      text
                      flat
                      no-caps
                      color="primary"
                      class="q-mx-md btn-underline"
                      @click="set_transactions_all_pages_selected(true)"
                    />
                  </template>

                  <template v-else>
                    <span v-html="i18n.transactions_selected_all.replace('%{0}', transactions.meta.count)" />

                    <q-btn
                      :label="i18n.transactions_select_none_action"
                      text
                      flat
                      no-caps
                      color="primary"
                      class="q-mx-md"
                      @click="set_transactions_all_pages_selected(false)"
                    />
                  </template>
                </div>
              </td>
            </tr>
          </template>

          <!-- Transactions Rows -->
          <template
            v-for="transaction in transactions.data"
            :key="transaction.movement_id"
          >
            <tr
              :class="{
                transaction_without_changerate: transaction.fiat_value == 0 || !transaction.fiat_value,
                'no-bottom-border': transaction.trade_in_id && transaction.is_trade_out,
                'transaction-row-active': transaction_details?.id == transaction.id,
              }"
              :style="hashBorderStyle(rowHash(transaction))"
              @click="transactions_details_sidebar_open(transaction.movement_id)"
              @contextmenu.prevent="openTransactionContextMenu($event, transaction)"
            >
              <!-- Selection -->
              <td>
                <q-checkbox
                  :model-value="!!transactions_selection[transaction.id]"
                  color="accent"
                  @update:model-value="(value, evt) => transactions_select_with_range(transaction, value, evt)"
                />
              </td>

              <!-- Date and Time -->
              <td>
                <div class="row items-center no-wrap">
                  <span
                    v-if="transaction.original_time"
                    class="modified-indicator"
                  >
                    <q-tooltip
                      anchor="top left"
                      self="bottom right"
                      :delay="500"
                      class="transaction-tooltip q-card shadow text-caption text-default q-pa-md q-menu"
                    >
                      {{ i18n.transaction_time_modified }}
                      <br>
                      {{ i18n.transaction_original_time }} {{ transaction.original_time_formatted }}
                    </q-tooltip>
                  </span>
                  <span
                    class="transaction-date"
                    @click.stop="filter_by_exact_date(transaction)"
                  >
                    {{ transaction.date }} <!-- Date (DD/MM/YY) -->
                  </span>
                  <span class="text-caption text-muted q-pl-xs">
                    {{ transaction.time }} <!-- Time (HH:mm) -->
                  </span>
                  <!-- Transaction that were added manually by the user -->
                  <q-avatar
                    v-if="transaction.manual"
                    color="pastel-blue"
                    size="18px"
                    class="q-ml-sm"
                  >
                    <q-icon
                      name="las la-hand-paper"
                      color="black"
                    />
                  </q-avatar>
                </div>
              </td>

              <!-- Category -->
              <td>
                <!-- When transfer is not justified (show uncategorised status) -->
                <template v-if="!transaction.justified && !transaction.kind">
                  <q-btn
                    :label="i18n.transactions_kinds_uncategorized"
                    color="primary"
                    unelevated
                    dense
                    size="sm"
                    no-caps
                    style="font-size: 13px"
                    @click.stop="transactions_kind_dialog_open(transaction)"
                  />
                </template>

                <template v-else>
                  <!-- When transfer is justified (show assigned kind) -->
                  <q-chip
                    :color="transaction.kind_icon.color"
                    :text-color="$q.dark.isActive ? transaction.kind_icon.color : 'black'"
                    clickable
                    flat
                    square
                    size="12px"
                    class="q-ma-none transaction-kind-chip"
                    :icon="transaction.kind_icon.class"
                    @click.stop="transactions_kind_dialog_open(transaction)"
                  >
                    {{ transaction.kind_label }}
                  </q-chip>
                </template>
              </td>

              <!-- Amount and asset -->
              <td class="align-center text-right">
                <span class="inline-flex items-center">
                  <span
                    v-if="transaction.original_amount"
                    class="modified-indicator"
                  >
                    <q-tooltip
                      anchor="top left"
                      self="bottom right"
                      :delay="500"
                      class="transaction-tooltip q-card shadow text-caption text-default q-pa-md q-menu"
                    >
                      {{ i18n.transaction_amount_modified }}
                      <br>
                      {{ i18n.transaction_original_amount }} {{ formatAmount(transaction.original_amount, transaction.asset_symbol || transaction.exchange_asset_symbol) }}
                    </q-tooltip>
                  </span>
                  <AssetChip
                    :amount="transaction.amount"
                    :amount_formatted="transaction.amount_formatted"
                    :asset_icon="transaction.asset_icon"
                    :symbol="transaction.asset_symbol"
                    :exchange_symbol="transaction.exchange_asset_symbol"
                    :asset_name="transaction.asset_name"
                    :excluded="transaction.exchange_asset_excluded"
                    :customized="transaction.exchange_asset_customized"
                    size="13"
                    no_margin
                  />
                </span>
              </td>

              <!-- Contre valeur EUR -->
              <td
                v-if="workspace_settings.settings_tr_counter_value_col"
                class="align-center text-right"
              >
                <span
                  v-if="transaction.fiat_value"
                  class=""
                >
                  <span v-if="transaction.has_wac_mapping && transaction.wac !== null && transaction.wac !== undefined">
                    <q-icon
                      v-if="transaction.fiat_amount"
                      name="circle"
                      size="8px"
                      class="q-mr-xs"
                      style="position: relative; top: -1px; color: #5fb3d9"
                    />
                    <span
                      v-if="parseFloat(transaction.wac) === 0"
                      class="text-negative"
                    >
                      0.00 EUR
                      <q-icon
                        name="warning"
                        size="14px"
                        class="q-ml-xs"
                        color="negative"
                        style="position: relative; top: -1px"
                      >
                        <q-tooltip
                          anchor="center left"
                          self="center right"
                          :delay="500"
                          class="q-card shadow text-caption text-default q-pa-md q-menu bg-negative text-white"
                          style="max-width: 400px"
                        >
                          <div class="text-bold q-mb-sm">
                            <q-icon
                              name="warning"
                              size="14px"
                              color="white"
                            />
                            {{ i18n.wac_zero_warning_title }}
                          </div>

                          <div class="q-mb-md">
                            {{ i18n.wac_zero_warning_explanation }}
                          </div>

                          <div class="q-mb-sm">
                            <strong>{{ i18n.wac_accounting_amount }}</strong><br>
                            {{ parseFloat(transaction.amount).toFixed(6) }} × 0.000000 = <strong>0.00 {{ i18n.fiat }}</strong>
                          </div>

                          <div class="text-bold q-mb-sm">
                            {{ i18n.wac_zero_warning_alert }}
                          </div>

                          <div
                            v-if="transaction.fiat_amount"
                            class="q-mt-md q-pt-md"
                            style="border-top: 1px solid rgba(255,255,255,0.3)"
                          >
                            <q-icon
                              name="circle"
                              size="8px"
                              style="position: relative; top: -1px; color: #5fb3d9"
                            />
                            <strong>{{ i18n.wac_manual_modification }}</strong><br>
                            {{ transaction.fiat_value_formatted }}<br>
                            <span class="text-caption">{{ i18n.wac_manual_modification_no_impact }}</span>
                          </div>

                          <div v-else>
                            <strong>{{ i18n.wac_market_value }}</strong><br>
                            {{ transaction.fiat_value_formatted }}
                          </div>
                        </q-tooltip>
                      </q-icon>
                    </span>
                    <span v-else>
                      {{ (parseFloat(transaction.amount) * parseFloat(transaction.wac)).toFixed(2) }} EUR
                      <q-icon
                        name="info"
                        size="14px"
                        class="q-ml-xs"
                        color="primary"
                        style="position: relative; top: -1px"
                      >
                        <q-tooltip
                          anchor="center left"
                          self="center right"
                          :delay="500"
                          class="q-card shadow text-caption text-default q-pa-md q-menu"
                          style="max-width: 400px"
                        >
                          <div class="text-bold q-mb-sm">
                            <q-icon
                              name="info"
                              size="14px"
                              color="primary"
                            />
                            {{ i18n.wac_amount_calculated }}
                          </div>

                          <div class="q-mb-md">
                            {{ i18n.wac_amount_explanation }}
                          </div>

                          <div class="q-mb-sm">
                            <strong>{{ i18n.wac_accounting_amount }}</strong><br>
                            {{ parseFloat(transaction.amount).toFixed(6) }} × {{ parseFloat(transaction.wac).toFixed(6) }} = <strong>{{ (parseFloat(transaction.amount) * parseFloat(transaction.wac)).toFixed(2) }} {{ i18n.fiat }}</strong>
                          </div>

                          <div
                            v-if="transaction.fiat_amount"
                            class="q-mt-md q-pt-md"
                            style="border-top: 1px solid #ccc"
                          >
                            <q-icon
                              name="circle"
                              size="8px"
                              style="position: relative; top: -1px; color: #5fb3d9"
                            />
                            <strong>{{ i18n.wac_manual_modification }}</strong><br>
                            {{ transaction.fiat_value_formatted }}<br>
                            <span class="text-caption ">{{ i18n.wac_manual_modification_no_impact }}</span>
                          </div>

                          <div
                            v-else
                            class=""
                          >
                            <strong>{{ i18n.wac_market_value }}</strong><br>
                            {{ transaction.fiat_value_formatted }}
                          </div>
                        </q-tooltip>
                      </q-icon>
                    </span>
                  </span>

                  <span v-else>
                    <q-icon
                      v-if="transaction.fiat_amount"
                      name="circle"
                      size="8px"
                      class="q-mr-xs"
                      style="position: relative; top: -1px; color: #5fb3d9"
                    />
                    {{ transaction.fiat_value_formatted }}
                  </span>
                  <q-tooltip
                    anchor="center left"
                    self="center right"
                    :delay="500"
                    class="q-card shadow text-caption text-default q-pa-md q-menu"
                  >
                    {{ i18n.transactions_counter_value_info }}

                    <span v-if="transaction.fiat_amount">
                      <br>

                      <q-icon
                        name="circle"
                        size="8px"
                        class=""
                        style="position: relative; top: -1px; color: #5fb3d9"
                      />
                      : {{ i18n.transactions_counter_value_changed }}
                    </span>
                  </q-tooltip>
                </span>
              </td>

              <!-- Wac -->
              <td
                v-if="workspace_settings.settings_tr_wac_col"
                class="align-center text-right"
              >
                <span
                  v-if="
                    transaction.wac &&
                      transaction.amount < 0 &&
                      transaction.kind_calculate_capital_gain_loss
                  "
                >
                  <q-icon
                    name="las la-calculator"
                    color="text-default"
                    size="14px"
                    class="q-ml-sm"
                  />
                  {{
                    transaction.wac !== undefined && transaction.wac !== null
                      ? parseFloat(transaction.wac).toFixed(2)
                      : "---"
                  }}

                  <q-tooltip
                    anchor="center left"
                    self="center right"
                    :delay="500"
                    class="transaction-tooltip q-card shadow text-caption text-default q-pa-md q-menu"
                  >
                    <q-item class="q-py-none">
                      <q-item-section avatar>
                        <q-avatar>
                          <q-icon name="las la-calculator" />
                        </q-avatar>
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ i18n.transactions_wac_info }}:
                          <b>{{ transaction.wac }}</b></q-item-label>
                        <q-item-label>
                          {{ i18n.transactions_wac_difference }}&nbsp;<b>{{ transaction.wac_difference_formatted }}</b>
                        </q-item-label>
                      </q-item-section>
                    </q-item>

                    <!-- fiat_value - wac * amount -->
                  </q-tooltip>
                </span>

                <span v-else>
                  {{
                    transaction.wac !== undefined && transaction.wac !== null
                      ? parseFloat(transaction.wac).toFixed(2)
                      : "---"
                  }}
                  <q-tooltip
                    v-if="
                      transaction.wac !== undefined && transaction.wac !== null
                    "
                    anchor="center left"
                    self="center right"
                    :delay="500"
                    class="transaction-tooltip q-card shadow text-caption text-default q-pa-md q-menu"
                  >
                    {{ i18n.transactions_wac_info }}:
                    <b>{{ transaction.wac }}</b>
                  </q-tooltip>
                </span>
              </td>

              <!-- Balance -->
              <td
                v-if="workspace_settings.settings_tr_balance_col"
                class="align-center text-right"
              >
                <q-chip
                  v-if="transaction.quantity_after_movement !== null"
                  square
                  color="transparent"
                  size="13"
                  clickable
                  :class="{
                    'text-negative': transaction.quantity_after_movement < 0,
                    'q-ma-none': true,
                  }"
                  @click.stop="copyBalanceToClipboard(transaction.quantity_after_movement)"
                >
                  {{
                    parseFloat(transaction.quantity_after_movement).toFixed(4) +
                      " " +
                      (transaction.asset_symbol
                        ? transaction.asset_symbol
                        : transaction.exchange_asset_symbol)
                  }}

                  <q-icon
                    v-if="transaction.quantity_after_movement < 0"
                    name="fas fa-circle-info"
                    size="18px"
                    class="q-ml-sm negative-balance-icon"
                    color="negative"
                  />

                  <!-- Tooltip with full value-->
                  <q-tooltip
                    anchor="center left"
                    self="center right"
                    :delay="500"
                    class="transaction-tooltip q-card shadow text-caption text-default q-pa-md q-menu"
                  >
                    {{ i18n.transactions_balance_info }}:
                    <b>{{ transaction.quantity_after_movement }}</b>
                    <!-- Message when quantity is negative -->
                    <template v-if="parseFloat(transaction.quantity_after_movement) < 0">
                      <br>
                      <span
                        class="text-bold"
                        v-html="i18n.transactions_warnings_negative_balance.replace(
                          '%{0}',
                          transaction.asset_symbol
                            ? transaction.asset_symbol
                            : transaction.exchange_asset_symbol,
                        )
                        "
                      />
                    </template>
                  </q-tooltip>
                </q-chip>
                <span v-else>---</span>
              </td>

              <!-- From address / Sender -->
              <td class="text-right">
                <template v-if="transaction.from_exchange_account">
                  <q-avatar
                    size="14px"
                    square
                    class="q-mr-xs"
                  >
                    <img
                      :alt="transaction.from_exchange_account.name"
                      :src="transaction.from_exchange_account.icon"
                    >
                  </q-avatar>
                  <span class="ellipsis max-w[25ch]">
                    {{ transaction.from_exchange_account.name }}
                  </span>
                  <q-tooltip
                    v-if="transaction.from_address"
                    :delay="500"
                  >
                    {{ transaction.from_address }}
                  </q-tooltip>
                </template>
                <template v-else>
                  <q-btn
                    v-if="transaction.from_address"
                    :label="address_mappings_map(transaction.from_address)"
                    color=""
                    flat
                    unelevated
                    no-caps
                    style="font-size: 13px"
                    class="q-px-none btn--no-hover"
                    @click.stop="
                      address_mappings_dialog_open(transaction.from_address, transaction)
                    "
                  />
                  <span v-else>{{ transaction.from_address }}</span>
                </template>
              </td>

              <!-- To address / Recipient -->
              <td class="justify-end text-right">
                <template v-if="transaction.to_exchange_account">
                  <q-avatar
                    size="14px"
                    square
                    class="q-mr-xs"
                  >
                    <img
                      :alt="transaction.to_exchange_account.name"
                      :src="transaction.to_exchange_account.icon"
                    >
                  </q-avatar>
                  <span class="ellipsis max-w[25ch]">
                    {{ transaction.to_exchange_account.name }}
                  </span>
                  <q-tooltip
                    v-if="transaction.to_address"
                    :delay="500"
                  >
                    {{ transaction.to_address }}
                  </q-tooltip>
                </template>
                <template v-else>
                  <q-btn
                    v-if="transaction.to_address"
                    :label="address_mappings_map(transaction.to_address)"
                    color=""
                    flat
                    unelevated
                    no-caps
                    style="font-size: 13px"
                    class="q-px-none btn--no-hover"
                    @click.stop="
                      address_mappings_dialog_open(transaction.to_address, transaction)
                    "
                  />
                  <span v-else>{{ transaction.to_address }}</span>
                </template>
              </td>

              <!-- Informations -->
              <td
                v-if="workspace_settings.settings_tr_info_col"
                class="text-right"
              >
                <!-- NOTE -->
                <q-icon
                  name="sym_o_sticky_note_2"
                  size="20px"
                  class="q-mr-sm"
                  :class="{
                    opacity_03: !transaction.note,
                  }"
                  :color="transaction.note ? 'accent' : 'primary'"
                >
                  <q-tooltip
                    v-if="transaction.note"
                    anchor="center left"
                    self="center right"
                    :delay="500"
                    class="transaction-tooltip q-card shadow text-caption text-default q-pa-md q-menu"
                  >
                    <p class="text-bold">
                      {{ i18n.transactions_note_label }}
                    </p>
                    {{ transaction.note }}
                  </q-tooltip>
                </q-icon>

                <!-- UPLOADS -->
                <q-icon
                  name="attach_file"
                  size="20px"
                  class="q-mr-sm"
                  :class="{
                    opacity_03: transaction.documents.length == 0,
                  }"
                  :color="transaction.documents.length > 0 ? 'accent' : 'primary'
                  "
                >
                  <q-tooltip
                    v-if="transaction.documents.length > 0"
                    anchor="center left"
                    self="center right"
                    :delay="500"
                    class="transaction-tooltip q-card shadow text-caption text-default q-pa-md q-menu"
                  >
                    <p class="text-bold">
                      {{ i18n.transactions_documents_uploaded_label }}
                    </p>
                    <template
                      v-for="document in transaction.documents"
                      :key="document"
                    >
                      {{ document }}

                      <br>
                    </template>
                  </q-tooltip>
                </q-icon>
              </td>

              <!-- Import Source -->
              <td
                v-if="workspace_settings.settings_tr_import_source_col"
                class="text-center"
              >
                {{ transaction.import_source_input_file }}
              </td>

              <td class="text-right actions-column">
                <q-btn
                  dense
                  flat
                  round
                  size="sm"
                  icon="sym_o_more_vert"
                  color="primary"
                  class="transaction-row-actions-btn"
                  :aria-label="i18n.transaction_context_open_menu"
                  @click.stop="openTransactionContextMenu($event, transaction)"
                />
              </td>
            </tr>

            <!-- Trade In -->
            <!-- Display associated trade_in if current transaction is a trade_out -->
            <tr
              v-if="transaction.trade_in_id && transaction.is_trade_out"
              style="position: relative"
              :class="{
                transaction_without_changerate:
                  transaction.fiat_value == 0 || !transaction.fiat_value,
                'transaction-row-active': transaction_details?.id == transaction.trade_in_id,
              }"
              :style="hashBorderStyle(tradeInRowHash(transaction))"
              @click="transactions_details_sidebar_open(transaction.trade_in_id)"
              @contextmenu.prevent="openTransactionContextMenu($event, buildTradeInContextTransaction(transaction))"
            >
              <!--Selection space  -->
              <td />

              <!-- Date and Time -->
              <td>
                <div class="row items-center no-wrap">
                  <span
                    v-if="transaction.trade_in_original_time"
                    class="modified-indicator"
                  >
                    <q-tooltip
                      anchor="top left"
                      self="bottom right"
                      :delay="500"
                      class="transaction-tooltip q-card shadow text-caption text-default q-pa-md q-menu"
                    >
                      {{ i18n.transaction_time_modified }}
                      <br>
                      {{ i18n.transaction_original_time }} {{ transaction.trade_in_original_time_formatted }}
                    </q-tooltip>
                  </span>
                  <span
                    class="transaction-date"
                    @click.stop="filter_by_exact_date(transaction, { date: transaction.trade_in_date })"
                  >
                    {{ transaction.trade_in_date }} <!-- Date (DD/MM/YY) -->
                  </span>
                  <span class="text-caption text-muted q-pl-xs">
                    {{ transaction.trade_in_time }} <!-- Time (HH:mm) -->
                  </span>
                  <!-- Transaction that were added manually by the user -->
                  <q-avatar
                    v-if="transaction.trade_in_manual"
                    color="pastel-blue"
                    size="18px"
                    class="q-ml-sm"
                  >
                    <q-icon
                      name="las la-hand-paper"
                      color="black"
                    />
                    <q-tooltip
                      anchor="top left"
                      self="bottom right"
                      :delay="500"
                      class="transaction-tooltip q-card shadow text-caption text-default q-pa-md q-menu"
                    >
                      {{ i18n.transactions_manual_desc }}
                    </q-tooltip>
                  </q-avatar>
                </div>
              </td>

              <!-- Category -->
              <td>
                <q-btn
                  :label="transaction.trade_in_kind_label"
                  color="primary"
                  flat
                  unelevated
                  no-wrap
                  no-caps
                  class="q-px-none btn--no-hover"
                  :ripple="false"
                  size="sm"
                  style="font-size: 12px; flex-flow: nowrap"
                  icon="subdirectory_arrow_right"
                />
              </td>

              <!-- Amount and asset -->
              <td class="align-center text-right">
                <span class="inline-flex items-center">
                  <span
                    v-if="transaction.trade_in_original_amount"
                    class="modified-indicator"
                  >
                    <q-tooltip
                      anchor="top left"
                      self="bottom right"
                      :delay="500"
                      class="transaction-tooltip q-card shadow text-caption text-default q-pa-md q-menu"
                    >
                      {{ i18n.transaction_amount_modified }}
                      <br>
                      {{ i18n.transaction_original_amount }} {{ formatAmount(transaction.trade_in_original_amount, transaction.trade_in_asset_symbol || transaction.trade_in_exchange_asset_symbol) }}
                    </q-tooltip>
                  </span>
                  <AssetChip
                    :amount="transaction.trade_in_amount"
                    :amount_formatted="transaction.trade_in_amount_formatted"
                    :asset_icon="transaction.trade_in_asset_icon"
                    :asset_name="transaction.trade_in_asset_name"
                    :symbol="transaction.trade_in_asset_symbol"
                    :exchange_symbol="transaction.trade_in_exchange_asset_symbol"
                    :excluded="transaction.trade_in_exchange_asset_excluded"
                    :customized="transaction.trade_in_exchange_asset_customized"
                    size="13"
                    no_margin
                  />
                </span>
              </td>

              <td
                v-if="
                  workspace_settings.settings_tr_counter_value_col"
                class="align-center text-right"
              >
                <span
                  v-if="transaction.trade_in_fiat_value"
                  class=""
                >
                  <q-icon
                    v-if="transaction.trade_in_fiat_amount"
                    name="circle"
                    size="8px"
                    class="q-mr-xs"
                    style="position: relative; top: -1px; color: #5fb3d9"
                  />
                  {{ transaction.trade_in_fiat_value_formatted }}
                  <q-tooltip
                    anchor="center left"
                    self="center right"
                    :delay="500"
                    class="q-card shadow text-caption text-default q-pa-md q-menu"
                  >
                    {{ i18n.transactions_counter_value_info }}

                    <span v-if="transaction.trade_in_fiat_amount">
                      <br>

                      <q-icon
                        name="circle"
                        size="8px"
                        class=""
                        style="position: relative; top: -1px; color: #5fb3d9"
                      />
                      : {{ i18n.transactions_counter_value_changed }}
                    </span>
                  </q-tooltip>
                </span>
              </td>

              <!-- trade_in_wac -->
              <td
                v-if="workspace_settings.settings_tr_wac_col"
                class="align-center text-right"
              >
                <span>
                  {{
                    transaction.trade_in_wac !== undefined &&
                      transaction.trade_in_wac !== null
                      ? parseFloat(transaction.trade_in_wac).toFixed(4)
                      : "---"
                  }}
                </span>
                <q-tooltip
                  v-if="
                    transaction.trade_in_wac !== undefined &&
                      transaction.trade_in_wac !== null
                  "
                  anchor="center left"
                  self="center right"
                  :delay="500"
                  class="transaction-tooltip q-card shadow text-caption text-default q-pa-md q-menu"
                >
                  {{ i18n.transactions_wac_info }}:
                  <b>{{ transaction.trade_in_wac }}</b>
                </q-tooltip>
              </td>

              <!-- Balance -->
              <td
                v-if="workspace_settings.settings_tr_balance_col"
                class="align-center text-right"
              >
                <span
                  :class="{
                    'text-negative':
                      transaction.trade_in_quantity_after_movement < 0,
                  }"
                >
                  {{
                    transaction.trade_in_quantity_after_movement !== null
                      ? parseFloat(
                        transaction.trade_in_quantity_after_movement
                      ).toFixed(4) +
                        " " +
                        (transaction.trade_in_asset_symbol
                          ? transaction.trade_in_asset_symbol
                          : transaction.trade_in_exchange_asset_symbol)
                      : "---"
                  }}

                  <q-icon
                    v-if="transaction.trade_in_quantity_after_movement < 0"
                    name="fas fa-circle-info"
                    size="18px"
                    class="q-ml-sm"
                    color="negative"
                    style="position: relative; top: -2px"
                  />

                  <q-tooltip
                    v-if="transaction.trade_in_quantity_after_movement !== null"
                    anchor="center left"
                    self="center right"
                    :delay="500"
                    class="transaction-tooltip q-card shadow text-caption text-default q-pa-md q-menu"
                  >
                    {{ i18n.transactions_balance_info }}:
                    <b>{{ transaction.trade_in_quantity_after_movement }}</b>

                    <!-- Message when quantity is negative -->
                    <template
                      v-if="
                        parseFloat(
                          transaction.trade_in_quantity_after_movement
                        ) < 0
                      "
                    >
                      <br>
                      <span
                        class="text-bold"
                        v-html="$t('accounting.transaction.alertNegativeBalance', [
                          transaction.trade_in_asset_symbol
                            ? transaction.trade_in_asset_symbol
                            : transaction.trade_in_exchange_asset_symbol,
                        ])
                        "
                      />
                    </template>
                  </q-tooltip>
                </span>
              </td>

              <!-- From address -->
              <td class="justify-end text-right">
                <q-btn
                  v-if="transaction.trade_in_from_address"
                  :label="address_mappings_map(transaction.trade_in_from_address)"
                  color=""
                  flat
                  unelevated
                  no-caps
                  style="font-size: 13px"
                  class="q-px-none btn--no-hover"
                  @click.stop="
                    address_mappings_dialog_open(
                      transaction.trade_in_from_address,
                      transaction
                    )
                  "
                />
                <span v-else />
              </td>

              <!-- To address -->
              <td class="text-right">
                <q-avatar
                  size="14px"
                  square
                  class="q-mr-xs"
                >
                  <img
                    :alt="transaction.trade_in_to_exchange_account.name"
                    :src="transaction.trade_in_to_exchange_account.icon"
                  >
                </q-avatar>
                <span class="ellipsis max-w[25ch]">
                  {{ transaction.trade_in_to_exchange_account.name }}
                </span>
                <q-tooltip
                  v-if="transaction.trade_in_to_address"
                  :delay="500"
                >
                  {{ transaction.trade_in_to_address }}
                </q-tooltip>
              </td>

              <!-- Informations -->
              <td
                v-if="workspace_settings.settings_tr_info_col"
                class="text-right"
              >
                <!-- ...existing trade-in informations content... -->
              </td>

              <!-- Import Source -->
              <td
                v-if="workspace_settings.settings_tr_import_source_col"
                class="text-center"
              >
                {{ transaction.trade_in_import_source }}
              </td>

              <td class="text-right actions-column" />
            </tr>

            <!-- FIX MARKET PRICE ALERT -->
            <tr
              v-if="transaction.change_rate_missing"
              :style="hashBorderStyle(transaction.blockchain_txid)"
            >
              <td
                colspan="100"
                class="table-select-all-row"
              >
                <div class="row wrap q-pa-xs">
                  <div class="text-secondary-caption q-mr-md">
                    <i class="material-icons q-mr-sm text-warning">warning</i>
                    <span v-html="missing_change_rate_html(transaction.exchange_asset_symbol)" />
                  </div>
                  <a
                    class="btn-underline cursor-pointer"
                    flat
                    dense
                    color="primary"
                    @click="transactions_missing_change_rate_dialog_open(transaction)"
                  >{{ i18n.transactions_warnings_missing_help }}</a>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
        <q-inner-loading
          :showing="transactions_loading"
          color="primary"
        >
          <q-spinner-ios size="50px" />
        </q-inner-loading>
      </q-markup-table>
      <q-menu
        ref="transactionContextMenu"
        v-model="contextMenu.visible"
        context-menu
        touch-position
        no-focus
        no-parent-event
        @hide="onContextMenuHide"
      >
        <q-list
          v-if="transactionContextMenuItems.length > 0"
          dense
          class="transaction-context-menu q-pa-sm"
        >
          <template
            v-for="item in transactionContextMenuItems"
            :key="item.key"
          >
            <q-separator
              v-if="item.type === 'separator'"
              spaced
              inset
            />
            <q-item
              v-else
              v-close-popup
              clickable
              @click="item.onClick && item.onClick()"
            >
              <q-item-section
                v-if="item.icon"
                avatar
              >
                <q-icon :name="item.icon" />
              </q-item-section>
              <q-item-section>{{ item.label }}</q-item-section>
            </q-item>
          </template>
        </q-list>
      </q-menu>
    </div>

    <!-- Table Bottom Pagination -->
    <div
      v-if="transactionCount > 0"
      class="q-table__bottom row items-center justify-end transactions-pagination-bottom"
    >
      <div class="q-table__control col-12">
        <Pagination
          :limits="[10, 20, 50, 100, 150, 200]"
          :meta="state_transactions_meta"
          :prop_keys="['transactions']"
          @click="transactions_pagination_update"
        />
      </div>
    </div>
  </div>
</template>

<script>
import AssetChip from '@/pages/w/transactions/asset_chip.vue';
import Pagination from '@/shared/pagination.vue';
import { generatePastelColorForHash } from '@/pages/w/transactions/hash_utils.js';

export default {
  name: 'TransactionsTable',
  components: {
    AssetChip,
    Pagination,
  },
  props: {
    transactions: {
      type: Object,
      required: true,
    },
    state_transactions_meta: {
      type: Object,
      required: true,
    },
    workspace_settings: {
      type: Object,
      required: true,
    },
    i18n: {
      type: Object,
      required: true,
    },
    transactions_loading: {
      type: Boolean,
      required: true,
    },
    state_search_params: {
      type: Object,
      required: true,
    },
    transactions_page_selected: {
      type: [Boolean, null],
      required: true,
    },
    transactions_all_pages_selected: {
      type: Boolean,
      required: true,
    },
    set_transactions_all_pages_selected: {
      type: Function,
      required: true,
    },
    transactions_selected_count: {
      type: Number,
      required: true,
    },
    transactions_selection: {
      type: Object,
      required: true,
    },
    transaction_details: {
      type: Object,
      required: false,
    },
    transactions_filter_add: {
      type: Function,
      required: true,
    },
    transactions_page_select: {
      type: Function,
      required: true,
    },
    transactions_select: {
      type: Function,
      required: true,
    },
    transactions_details_sidebar_open: {
      type: Function,
      required: true,
    },
    transactions_kind_dialog_open: {
      type: Function,
      required: true,
    },
    formatAmount: {
      type: Function,
      required: true,
    },
    copyBalanceToClipboard: {
      type: Function,
      required: true,
    },
    address_mappings_map: {
      type: Function,
      required: true,
    },
    address_mappings_dialog_open: {
      type: Function,
      required: true,
    },
    missing_change_rate_html: {
      type: Function,
      required: true,
    },
    transactions_missing_change_rate_dialog_open: {
      type: Function,
      required: true,
    },
    filter_by_exact_date: {
      type: Function,
      required: true,
    },
    buildTransactionContextMenuItems: {
      type: Function,
      required: true,
    },
    transactions_pagination_update: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      contextMenu: {
        visible: false,
        transaction: null,
      },
    };
  },
  computed: {
    transactionCount() {
      return this.transactions?.meta?.count || 0;
    },
    transactionHashCounts() {
      const counts = {};
      const addHash = (hash) => {
        const normalizedHash = this.normalizeHash(hash);
        if (!normalizedHash) {
          return;
        }
        counts[normalizedHash] = (counts[normalizedHash] || 0) + 1;
      };

      (this.transactions?.data || []).forEach((transaction) => {
        addHash(this.rowHash(transaction));
        if (transaction.trade_in_id && transaction.is_trade_out) {
          addHash(this.tradeInRowHash(transaction));
        }
      });

      return counts;
    },
    transactionHashColorMap() {
      const colors = {};
      Object.keys(this.transactionHashCounts).forEach((hash) => {
        colors[hash] = generatePastelColorForHash(hash);
      });

      return colors;
    },
    transactionContextMenuItems() {
      if (!this.contextMenu.transaction) {
        return [];
      }
      if (typeof this.buildTransactionContextMenuItems !== 'function') {
        return [];
      }
      return this.buildTransactionContextMenuItems(this.contextMenu.transaction) || [];
    },
  },
  methods: {
    transactions_select_with_range(transaction, checked, event) {
      if (!event?.shiftKey || checked !== true) {
        this.transactions_select(transaction, checked);
        return;
      }

      const transactions = this.transactions?.data || [];
      const clickedIndex = transactions.findIndex((t) => t.id === transaction.id);
      if (clickedIndex === -1) {
        this.transactions_select(transaction, checked);
        return;
      }

      const selectedIndices = transactions
        .map((t, index) => ({ index, checked: !!this.transactions_selection[t.id] }))
        .filter(({ checked, index }) => checked && index !== clickedIndex)
        .map(({ index }) => index);

      if (selectedIndices.length === 0) {
        this.transactions_select(transaction, checked);
        return;
      }

      let closestIndex = selectedIndices[0];
      let closestDistance = Math.abs(closestIndex - clickedIndex);

      selectedIndices.slice(1).forEach((index) => {
        const distance = Math.abs(index - clickedIndex);

        if (distance < closestDistance) {
          closestIndex = index;
          closestDistance = distance;
          return;
        }

        if (distance === closestDistance && index < closestIndex) {
          closestIndex = index;
        }
      });

      const start = Math.min(closestIndex, clickedIndex);
      const end = Math.max(closestIndex, clickedIndex);

      for (let index = start; index <= end; index += 1) {
        this.transactions_select(transactions[index], true);
      }
    },
    rowHash(transaction) {
      if (!transaction) {
        return null;
      }
      return transaction.blockchain_txid
        || transaction.trade_in_blockchain_txid
        || transaction.trade_out_blockchain_txid
        || null;
    },
    tradeInRowHash(transaction) {
      if (!transaction || !transaction.trade_in_id || !transaction.is_trade_out) {
        return null;
      }
      return transaction.trade_in_blockchain_txid
        || transaction.blockchain_txid
        || transaction.trade_out_blockchain_txid
        || null;
    },
    buildTradeInContextTransaction(transaction) {
      if (!transaction || !transaction.trade_in_id || !transaction.is_trade_out) {
        return transaction;
      }
      return {
        ...transaction,
        movement_id: transaction.trade_in_id,
        blockchain_txid: transaction.trade_in_blockchain_txid,
        amount: transaction.trade_in_amount,
        amount_formatted: transaction.trade_in_amount_formatted,
        asset_id: transaction.trade_in_asset_id,
        asset_symbol: transaction.trade_in_asset_symbol,
        asset_name: transaction.trade_in_asset_name,
        exchange_asset_id: transaction.trade_in_exchange_asset_id,
        exchange_asset_symbol: transaction.trade_in_exchange_asset_symbol,
        exchange_asset_name: transaction.trade_in_exchange_asset_name,
        date: transaction.trade_in_date,
        time: transaction.trade_in_time,
        datetime: transaction.trade_in_datetime,
        from_address: transaction.trade_in_from_address,
        to_address: transaction.trade_in_to_address,
        from_exchange_account: transaction.trade_in_from_exchange_account,
        to_exchange_account: transaction.trade_in_to_exchange_account,
        kind: transaction.trade_in_transaction_kind_id,
        kind_label: transaction.trade_in_kind_label,
        manual: transaction.trade_in_manual,
        original_amount: transaction.trade_in_original_amount,
        original_time: transaction.trade_in_original_time,
        original_time_formatted: transaction.trade_in_original_time_formatted,
        fiat_value_formatted: transaction.trade_in_fiat_value_formatted || transaction.fiat_value_formatted,
        wac: transaction.trade_in_wac ?? transaction.wac,
        trade_in_wac: transaction.trade_in_wac,
      };
    },
    normalizeHash(hash) {
      if (!hash || typeof hash !== 'string') {
        return null;
      }
      const normalized = hash.trim().toLowerCase();
      return normalized.length > 0 ? normalized : null;
    },
    transactionHashColor(hash) {
      const normalizedHash = this.normalizeHash(hash);
      if (!normalizedHash) {
        return null;
      }
      return this.transactionHashColorMap[normalizedHash] || null;
    },
    hashBorderStyle(hash) {
      const normalizedHash = this.normalizeHash(hash);
      if (!normalizedHash) {
        return {};
      }
      const hasMultiple = (this.transactionHashCounts[normalizedHash] || 0) > 1;
      if (!hasMultiple) {
        return {};
      }
      const color = this.transactionHashColor(normalizedHash);
      if (!color) {
        return {};
      }
      return {
        borderLeft: `4px solid ${color}`,
      };
    },
    openTransactionContextMenu(event, transaction) {
      if (!transaction) {
        return;
      }
      if (event?.type === 'contextmenu' && typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      this.contextMenu.transaction = transaction;
      this.contextMenu.visible = false;
      this.$nextTick(() => {
        const menu = this.$refs.transactionContextMenu;
        if (menu?.show) {
          menu.show(event);
        }
      });
    },
    onContextMenuHide() {
      this.contextMenu.transaction = null;
      this.contextMenu.visible = false;
    },
  },
};
</script>

<style scoped>
.transactions-pagination-bottom {
  /* Leave room for the sticky bulk edit toolbar */
  padding-bottom: 96px;
}

.transactions-pagination-top {
  position: sticky;
  top: 64px;
  z-index: 5;
  background-color: var(--page-background);
}

.transaction-row-active td {
  background-color: #f5f5f5 !important;
}

.body--dark .transaction-row-active td {
  background-color: var(--surface-pressed) !important;
}

.actions-column {
  width: 48px;
}

.transaction-row-actions-btn {
  min-width: 32px;
}

.transaction-context-menu {
  min-width: 240px;
}

.transaction-date {
  color: var(--text-default);
  cursor: pointer;
  text-decoration: none;
}

.transaction-date:hover {
  text-decoration: underline;
}

.negative-balance-icon {
  align-self: center;
  display: inline-flex;
}
</style>
